import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { cardStyleClasses, isDarkBackground } from "../cardStyles";
import { SectionShell } from "../SectionShell";

const featureItemSchema = z.object({
  icon: z.string().optional(),
  title: z.string().default("Feature"),
  body: z.string().default("Description of this feature."),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Features"),
  subheading: z.string().optional(),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  cardStyle: z.enum(["elevated", "flat", "outlined"]).default("elevated"),
  iconSize: z.enum(["small", "medium", "large"]).default("medium"),
  textAlign: z.enum(["center", "left"]).default("center"),
  items: z
    .array(featureItemSchema)
    .default([
      { icon: "⚡", title: "Fast & Easy", body: "Get started in minutes with our streamlined process." },
      { icon: "🛡️", title: "Trusted Experts", body: "Work with experienced professionals you can rely on." },
      { icon: "💰", title: "Transparent Pricing", body: "No hidden fees. Pay only for what you use." },
    ]),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

const iconSizeClasses: Record<string, string> = {
  small: "text-2xl",
  medium: "text-4xl",
  large: "text-5xl",
};

const colClasses: Record<number, string> = {
  2: "grid-cols-1 @md:grid-cols-2",
  3: "grid-cols-1 @md:grid-cols-3",
  4: "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-4",
};

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);
  const darkFlat = safe.cardStyle === "flat" && isDarkBackground(safe.backgroundStyle);
  const iconClass = iconSizeClasses[safe.iconSize] || iconSizeClasses.medium;
  const isCenter = safe.textAlign === "center";

  return (
    <SectionShell globals={safe}>
        <div className={`${isCenter ? "text-center" : "text-left"} mb-12`}>
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className={`text-3xl @md:text-[2.5rem] font-bold mb-4${isCenter ? "" : ""}`}
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className={`text-lg opacity-80 max-w-2xl${isCenter ? " mx-auto" : ""}`}
              data-prop="subheading"
            />
          )}
        </div>
        <div
          className={`grid gap-8 ${colClasses[safe.columns] || colClasses[3]}`}
        >
          {safe.items.map((item, i) => (
            <div
              key={i}
              className={`${cardStyleClasses(safe.cardStyle, safe.backgroundStyle)}${isCenter ? " text-center" : ""}`}
            >
              {item.icon && (
                <div className={`${iconClass} mb-4`}>{item.icon}</div>
              )}
              <RichText
                as={SubH}
                html={item.title}
                className={`text-xl font-semibold mb-2 ${darkFlat ? "opacity-90" : "text-text"}`}
                data-prop={`items.${i}.title`}
              />
              <RichText
                as="p"
                html={item.body}
                className={darkFlat ? "opacity-70" : "text-text-muted"}
                data-prop={`items.${i}.body`}
              />
            </div>
          ))}
        </div>
    </SectionShell>
  );
}
